import { useMemo, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useTourGuide, TourGuideConfig } from "./useTourGuide";
import { useAutoTourGuide } from "./useAutoTourGuide";
import { useTourGuideContext } from "@root/context/tour-guide-context";
import { updateTourCompletion } from "@store/thunks/userProfile";
import { ROUTE_NAMES } from "@root/constants";
import { TourGuideType } from "@root/constants/tour-guide";
import useMediaQ from "@root/hooks/useMediaQ";

/**
 * Tour Guide Hook for Journalist Update Profile
 *
 * Steps cover all form fields and actions with clear prerequisites and after-effects.
 */
export const useJournalistUpdateProfileTourGuide = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { setActiveTourModule, setCurrentTourStep } = useTourGuideContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;

  const isOnUpdateProfile = pathname === ROUTE_NAMES.JOURNALIST_UPDATE_PROFILE;

  const config: TourGuideConfig = useMemo(
    () => ({
      moduleId: "journalistUpdateProfile",
      enabled: isOnUpdateProfile,
      steps: [
        {
          id: "update-profile-change-history",
          targetSelector:
            '[data-tour-item="journalist-update-profile-change-history"]',
          title: t("tourGuide.journalistUpdateProfile.step1.title"),
          description: t("tourGuide.journalistUpdateProfile.step1.description"),
          position: "bottom",
          offset: { x: 0, y: 12 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-avatar",
          targetSelector: '[data-tour-item="journalist-update-profile-avatar"]',
          title: t("tourGuide.journalistUpdateProfile.step2.title"),
          description: t("tourGuide.journalistUpdateProfile.step2.description"),
          position: "bottom",
          offset: { x: 0, y: 12 },
          highlightPadding: 12,
        },
        {
          id: "update-profile-name",
          targetSelector: '[data-tour-item="journalist-update-profile-name"]',
          title: t("tourGuide.journalistUpdateProfile.step3.title"),
          description: t("tourGuide.journalistUpdateProfile.step3.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-email",
          targetSelector: '[data-tour-item="journalist-update-profile-email"]',
          title: t("tourGuide.journalistUpdateProfile.step4.title"),
          description: t("tourGuide.journalistUpdateProfile.step4.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-phone",
          targetSelector: '[data-tour-item="journalist-update-profile-phone"]',
          title: t("tourGuide.journalistUpdateProfile.step5.title"),
          description: t("tourGuide.journalistUpdateProfile.step5.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-organization-type",
          targetSelector:
            '[data-tour-item="journalist-update-profile-organization-type"]',
          title: t("tourGuide.journalistUpdateProfile.step6.title"),
          description: t("tourGuide.journalistUpdateProfile.step6.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-company",
          targetSelector:
            '[data-tour-item="journalist-update-profile-company"]',
          title: t("tourGuide.journalistUpdateProfile.step7.title"),
          description: t("tourGuide.journalistUpdateProfile.step7.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-department",
          targetSelector:
            '[data-tour-item="journalist-update-profile-department"]',
          title: t("tourGuide.journalistUpdateProfile.step8.title"),
          description: t("tourGuide.journalistUpdateProfile.step8.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-role",
          targetSelector: '[data-tour-item="journalist-update-profile-role"]',
          title: t("tourGuide.journalistUpdateProfile.step9.title"),
          description: t("tourGuide.journalistUpdateProfile.step9.description"),
          position: "right",
          offset: { x: 12, y: 0 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-change-password",
          targetSelector:
            '[data-tour-item="journalist-update-profile-change-password"]',
          title: t("tourGuide.journalistUpdateProfile.step10.title"),
          description: t(
            "tourGuide.journalistUpdateProfile.step10.description",
          ),
          position: "top",
          offset: { x: 0, y: -12 },
          highlightPadding: 10,
        },
        {
          id: "update-profile-save",
          targetSelector: '[data-tour-item="journalist-update-profile-save"]',
          title: t("tourGuide.journalistUpdateProfile.step11.title"),
          description: t(
            "tourGuide.journalistUpdateProfile.step11.description",
          ),
          position: "top",
          offset: { x: 0, y: -12 },
          highlightPadding: 10,
        },
      ],
    }),
    [isOnUpdateProfile, t],
  );

  const baseTourGuide = useTourGuide(config);

  const handleTourComplete = () => {
    dispatch(
      updateTourCompletion({
        payload: {
          tourGuides: [
            { type: TourGuideType.JournalistUpdateProfile, isDone: true },
          ],
        },
        onSuccess: () => {},
        onError: () => {},
      }) as any,
    );
  };

  const journalistUpdateProfileTourGuide = useAutoTourGuide({
    tourGuide: baseTourGuide,
    tourGuideType: TourGuideType.JournalistUpdateProfile,
    isOnTourPath: isOnUpdateProfile && !isMobile,
    onTourComplete: handleTourComplete,
  });

  useEffect(() => {
    if (journalistUpdateProfileTourGuide.isActive) {
      setActiveTourModule("journalistUpdateProfile");
      setCurrentTourStep(journalistUpdateProfileTourGuide.currentStep);
    } else {
      setActiveTourModule(null);
    }
  }, [
    journalistUpdateProfileTourGuide.isActive,
    journalistUpdateProfileTourGuide.currentStep,
    setActiveTourModule,
    setCurrentTourStep,
  ]);

  return {
    journalistUpdateProfileTourGuide,
    isMobile,
  };
};
